import React, { useState } from "react";
import { Plus, X, PlayCircle } from "@phosphor-icons/react";

/**
 * VideoUrlsInput — admin control for managing a list of video embed URLs.
 *
 * Accepts YouTube, Vimeo, Facebook, Instagram, TikTok URLs (or direct
 * mp4/webm links). Detects platform from the URL on the fly.
 */

function detectPlatform(url) {
  if (!url) return null;
  if (/youtu\.?be/i.test(url)) return "YouTube";
  if (/vimeo\.com/i.test(url)) return "Vimeo";
  if (/(?:facebook\.com|fb\.watch)/i.test(url)) return "Facebook";
  if (/instagram\.com/i.test(url)) return "Instagram";
  if (/tiktok\.com/i.test(url)) return "TikTok";
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return "Direct file";
  return "Unknown";
}

export default function VideoUrlsInput({ value = [], onChange, label = "Videos" }) {
  const [draft, setDraft] = useState("");
  const items = Array.isArray(value) ? value : [];

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...items, v]);
    setDraft("");
  };

  const remove = (i) => {
    const next = items.slice();
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div data-testid="video-urls-input">
      <div className="admin-label">{label}</div>
      <p className="text-white/40 text-xs mt-1 mb-3">
        Paste a video URL from YouTube, Vimeo, Facebook, Instagram, or TikTok. Press Enter or click Add.
      </p>

      <div className="flex gap-2">
        <input
          className="admin-input flex-1"
          placeholder="https://www.youtube.com/watch?v=..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          data-testid="video-url-input"
        />
        <button
          type="button"
          onClick={add}
          className="px-4 bg-atc-red text-white font-heading uppercase tracking-widest text-xs border-2 border-atc-red hover:bg-atc-black hover:border-atc-black transition-colors"
          data-testid="video-url-add"
        >
          <Plus size={16} weight="bold" />
        </button>
      </div>

      {items.length > 0 && (
        <ul className="mt-4 space-y-2">
          {items.map((u, i) => (
            <li
              key={i}
              className="flex items-center gap-3 bg-[#1f1f1f] border border-[#333] p-3"
              data-testid={`video-url-row-${i}`}
            >
              <PlayCircle size={20} weight="bold" className="text-atc-red shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {detectPlatform(u)}
                </div>
                <div className="text-white text-sm truncate">{u}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-white/40 hover:text-atc-red transition-colors"
                data-testid={`video-url-remove-${i}`}
              >
                <X size={18} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
